import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

// API
import { useDeleteListItem } from '../../api/hooks/useDeleteListItem';

// Config
import { uiConfig, crudConfig } from '../../../conf/listItems';
import { listTypes } from '../../../conf/enums';

// Components
import Table from '../../../../components/Table';
import { Button } from '../../../../components/Button';
import { ConceptTableModal } from '../conceptTableModal';
import { ListItem } from '../listItem';
import { DeleteListItem } from '../deleteListItem';

// Utils
import { translateUIConfigForTable } from '../../../../common/utility';

const { READ_ONLY_FEATURES } = process.env;

const TableContainer = styled.div`
    margin-left: -1rem;
    margin-right: -1rem;
`;

export const ListItemsTable = ({ data, sessionToken, onSave }) => {
    const [conceptLookup, setConceptLookup] = React.useState(false);
    const saveDisabled = READ_ONLY_FEATURES === 'true';
    const [editing, setEditing] = React.useState(null);
    const [deleting, setDeleting] = React.useState(null);
    const { columns, actions, initialState } = translateUIConfigForTable(uiConfig, setEditing, setDeleting);
    const items = React.useMemo(() => {
        return data?.cdsListItems ?? [];
    }, [data]);
    const revisedActions = React.useMemo(() => {
        if (!actions || typeof uiConfig.actionConditions !== 'object') {
            return actions;
        }
        return actions.filter(({ type }) => {
            return Object.hasOwn(uiConfig.actionConditions, type) ? uiConfig.actionConditions[type](data) : true;
        });
    }, [actions, data]);
    const controlButtons = [];
    switch (data.listType) {
        case listTypes.AD_HOC:
            controlButtons.push(
                <Button key={'add-new'} appearance="primary" onClick={() => setEditing('new')}>
                    Add List Item
                </Button>
            );
            break;
        case listTypes.AD_HOC_CONCEPT:
            controlButtons.push(
                <Button key={'add-new'} appearance="primary" onClick={() => setConceptLookup(true)}>
                    Edit List Item(s)
                </Button>
            );
            break;
        default:
            break;
    }

    const selectedItem = React.useMemo(() => {
        if (!editing) return null;
        return (items ?? []).find((item) => {
            return item[uiConfig.primaryKey] === editing;
        });
    }, [items, editing]);

    const tableData = React.useMemo(() => {
        if (!items) return [];
        const transformedItems =
            revisedActions != null
                ? items.map((v) => ({
                      ...v,
                      DataTableActions: revisedActions.map((i) => {
                          return {
                              ...i,
                              action: () => {
                                  i.action(v[uiConfig.primaryKey]);
                              }
                          };
                      })
                  }))
                : items;
        return transformedItems.filter(({ dtoState }) => dtoState !== 'DELETED');
    }, [items, revisedActions]);

    const deleteData = React.useMemo(() => {
        if (!deleting) return null;
        return items.find(({ uuid }) => uuid === deleting);
    }, [deleting]);

    const onDelete = (newCdsListItem) => {
        const itemsCopy = [...items];
        const itemIndex = itemsCopy.findIndex(({ uuid }) => uuid === newCdsListItem.uuid);

        if (itemIndex >= 0) {
            if (itemsCopy[itemIndex]?.dtoState === 'NEW') {
                itemsCopy.splice(itemIndex, 1);
            } else {
                itemsCopy[itemIndex] = {
                    ...newCdsListItem,
                    dtoState: 'DELETED'
                };
            }
        }

        onSave({
            cdsListItems: itemsCopy,
            dtoState: 'UPDATED'
        });
    };

    const isExistingDoc = (doc) => {
        return (
            (items ?? []).find((v) => {
                return v.itemId !== doc.itemId && v.adHocId === doc.adHocId && v.adHocLabel === doc.adHocLabel;
            }) !== undefined
        );
    };

    return (
        <TableContainer>
            <Table
                withHeader={true}
                columns={columns}
                data={tableData}
                withActions={revisedActions !== null}
                id={'list-items-manager'}
                label={'List Items'}
                showControlDeck={true}
                paginated={true}
                controlButtons={controlButtons}
                hiddenControls={{
                    pageSize: true
                }}
                initialState={{
                    ...initialState,
                    pageSize: 8
                }}
            />
            {(selectedItem !== null || editing === 'new') && (
                <ListItem
                    isNew={editing === 'new'}
                    type={data.listType}
                    listId={data?.listId}
                    saveDisabled={saveDisabled}
                    data={selectedItem}
                    onApply={(newCdsListItem) => {
                        const itemsCopy = [...items];
                        const itemIndex = itemsCopy.findIndex(({ uuid }) => uuid === newCdsListItem.uuid);

                        if (itemIndex >= 0) {
                            itemsCopy[itemIndex] = newCdsListItem;
                        } else {
                            itemsCopy.push(newCdsListItem);
                        }
                        onSave({
                            cdsListItems: itemsCopy,
                            dtoState: 'UPDATED'
                        });
                    }}
                    isExistingDoc={isExistingDoc}
                    closeDialog={() => {
                        setEditing(null);
                    }}
                    sessionToken={sessionToken}
                    onMount={typeof uiConfig.onFormMount === 'function' ? uiConfig.onFormMount : null}
                />
            )}
            {conceptLookup ? (
                <ConceptTableModal
                    sessionToken={sessionToken}
                    closeDialog={() => {
                        setConceptLookup(false);
                    }}
                    listId={data?.listId}
                    currentListItems={data?.cdsListItems ?? []}
                    onApply={(newCdsListItems) => {
                        onSave({
                            cdsListItems: newCdsListItems,
                            dtoState: 'UPDATED'
                        });
                    }}
                />
            ) : null}
            {deleting != null && (
                <DeleteListItem
                    data={deleteData}
                    onDelete={onDelete}
                    closeDialog={() => setDeleting(null)}
                    sessionToken={sessionToken}
                />
            )}
        </TableContainer>
    );
};

export default ListItemsTable;

ListItemsTable.propTypes = {
    data: PropTypes.object,
    items: PropTypes.arrayOf(PropTypes.object),
    sessionToken: PropTypes.string,
    listType: PropTypes.string,
    onSave: PropTypes.func
};
